import {FormEvent, useCallback, useMemo, useState} from "react";
import {styled} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@memaro/components/Button.tsx";
import Form from "@memaro/components/Form.tsx";
import Alert from "@mui/material/Alert";
import {
    EmptySignInPassword,
    EmptySignInUsername,
    NotAuthorizedException,
    StepDone,
    nameFromError
} from "@memaro/auth";
import {useFormInput} from "@memaro/forms";
import {useRouter} from "../hooks/useRouter.ts";
import {useAuth} from "../lib/auth.ts";
import Divider from "@mui/material/Divider";
import GoogleIcon from "@mui/icons-material/Google";
import {unauthenticated} from "../hoc/auth.tsx";

const StyledStack = styled(Stack)({
    width: 'fit-content',
    margin: '0 auto'
});

const StyledTextField = styled(TextField)({
    marginTop: 30,
    minWidth: 300,
    fontFamily: "inherit"
})

const LoginPage = unauthenticated(() => {
    const {gotoRegister, gotoResetPassword, gotoHome, gotoRedirect} = useRouter();
    const {signIn, signInWithRedirect, signUpUsername} = useAuth();

    const [email, setEmail] = useFormInput(signUpUsername)
    const [password, setPassword] = useFormInput()
    const [error, setError] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)

    const handleLogin = useCallback(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true)
        try {
            const {nextStep} = await signIn({
                username: email || "",
                password: password || "",
            })
            // sign in changes the auth state, which automatically triggers a redirect to the correct page
            switch (nextStep.signInStep) {
                case StepDone:
                    if (!gotoRedirect()) {
                        gotoHome()
                    }
                    break
                default:
                    console.error("Unknown step:", nextStep)
            }
        } catch (e: unknown) {
            setError(nameFromError(e))
        } finally {
            setLoading(false)
        }
    }, [email, gotoHome, gotoRedirect, password, signIn])

    const handleSignInWithGoogle = useCallback(() => signInWithRedirect({
        provider: 'Google'
    }), [signInWithRedirect])

    const errorText = useMemo(() => {
        switch (error) {
            case "":
                return ""
            case EmptySignInUsername:
                return "Geen email adres opgegeven"
            case EmptySignInPassword:
                return "Geen wachtwoord opgegeven"
            case NotAuthorizedException:
                return "Geen gebruiker gevonden voor email adres + wachtwoord combinatie"
            default:
                console.error("Unknown error:", error)
                return "Onbekende fout opgetreden"
        }
    }, [error])

    return (
        <StyledStack>
            <Form onSubmit={handleLogin}>
                <h1>Inloggen</h1>
                {errorText ? <Alert severity="error">{errorText}</Alert> : null}
                <StyledTextField type="email" label="Email adres" autoComplete="username" value={email} onChange={setEmail} autoFocus/>
                <StyledTextField type="password" label="Wachtwoord" autoComplete="password" value={password} onChange={setPassword}/>
                <Box sx={{display: "flex", flexDirection: "row-reverse",}}>
                    <Box>
                        <Button sx={{marginTop: "10px"}} onClick={gotoResetPassword} disabled={loading}>Wachtwoord vergeten?</Button>
                        <Button sx={{marginTop: "10px", marginLeft: "10px"}} onClick={gotoRegister} disabled={loading}>Registreren</Button>
                        <Button color="primary" type="submit" sx={{marginTop: "10px", marginLeft: "10px"}} loading={loading}>Inloggen</Button>
                    </Box>
                </Box>
                <Divider sx={{marginTop: 2, marginBottom: 2}}/>
                <Button onClick={handleSignInWithGoogle}>
                    <GoogleIcon sx={{marginRight: 1}}/>
                    Inloggen met Google
                </Button>
            </Form>
        </StyledStack>
    )
})

export default LoginPage
